import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { Input, Button as DSButton } from '@coffeeplexity/ui';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Coffee } from 'lucide-react-native';
import {
  passwordResetRequestSchema,
  PasswordResetRequestData,
} from '@coffeeplexity/shared';
import { useRequestPasswordReset } from '@coffeeplexity/api-client';
import Toast from 'react-native-toast-message';

// Utility functions for responsive design
const isTablet = (width: number): boolean => width >= 768;
const isLandscape = (width: number, height: number): boolean => width > height;

interface ResponsiveStyles {
  scrollContent: ViewStyle;
  header: ViewStyle;
  iconSize: number;
  title: TextStyle;
  subtitle: TextStyle;
  form: ViewStyle;
  inputContainer: ViewStyle;
  resetButton: ViewStyle;
  helpContainer: ViewStyle;
  helpText: TextStyle;
}

export function ForgotPasswordScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const requestResetMutation = useRequestPasswordReset();
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const windowDimensions = useWindowDimensions();

  // Responsive layout detection
  const isTabletDevice = isTablet(windowDimensions.width);
  const isLandscapeMode = isLandscape(
    windowDimensions.width,
    windowDimensions.height
  );
  const screenWidth = windowDimensions.width;
  const isSmallScreen = screenWidth < 375;
  const isLargeScreen = screenWidth > 768;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<PasswordResetRequestData>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: PasswordResetRequestData) => {
    try {
      await requestResetMutation.mutateAsync(data.email);
      setSentEmail(data.email);
      setEmailSent(true);
      Toast.show({
        type: 'success',
        text1: 'Reset Email Sent',
        text2: 'Check your email for password reset instructions.',
      });
      navigation.goBack();
    } catch (error: any) {
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: error.response?.data?.detail || 'Failed to send reset email.',
      });
    }
  };

  // Responsive styles
  const responsiveStyles = getResponsiveStyles(
    isTabletDevice,
    isLandscapeMode,
    isSmallScreen,
    isLargeScreen
  );

  return (
    <SafeAreaView
      style={{
        ...styles.container,
        backgroundColor: theme.colors.background,
      }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView
          contentContainerStyle={{
            ...styles.scrollContent,
            ...responsiveStyles.scrollContent,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={{ ...styles.header, ...responsiveStyles.header }}>
            <Coffee
              size={responsiveStyles.iconSize}
              color={theme.colors.primary}
            />
            <Text
              variant={isLargeScreen ? 'displaySmall' : 'headlineLarge'}
              style={{
                ...styles.title,
                ...responsiveStyles.title,
                color: theme.colors.onBackground,
              }}
            >
              Forgot Password
            </Text>
            <Text
              variant={isSmallScreen ? 'bodyMedium' : 'bodyLarge'}
              style={{
                ...styles.subtitle,
                ...responsiveStyles.subtitle,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Enter your email to receive reset instructions
            </Text>
          </View>

          {/* Form */}
          <View style={{ ...styles.form, ...responsiveStyles.form }}>
            <Controller
              control={control}
              name='email'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Email'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType='email-address'
                    autoCapitalize='none'
                    autoComplete='email'
                    error={errors.email?.message}
                  />
                </View>
              )}
            />

            <DSButton
              variant='primary'
              onPress={handleSubmit(onSubmit)}
              loading={requestResetMutation.isPending}
              disabled={requestResetMutation.isPending}
              style={{
                ...styles.resetButton,
                ...responsiveStyles.resetButton,
              }}
            >
              Send Reset Email
            </DSButton>
          </View>

          {/* Help Text */}
          <View
            style={{
              ...styles.helpContainer,
              ...responsiveStyles.helpContainer,
            }}
          >
            <Text
              style={{
                ...styles.helpText,
                ...responsiveStyles.helpText,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Remember your password?{' '}
            </Text>
            <DSButton
              variant='ghost'
              onPress={() => navigation.navigate('login' as never)}
              style={styles.signInButton}
            >
              Sign In
            </DSButton>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

/**
 * Generates responsive styles based on device characteristics
 * Ensures consistent layout across mobile, tablet, and web platforms
 */
function getResponsiveStyles(
  isTablet: boolean,
  isLandscape: boolean,
  isSmallScreen: boolean,
  isLargeScreen: boolean
): ResponsiveStyles {
  const baseIconSize = 48;
  const tabletIconSize = 64;
  const landscapeIconSize = 40;

  return {
    scrollContent: {
      paddingHorizontal: isSmallScreen ? 16 : isLargeScreen ? 48 : 24,
      paddingVertical: isLandscape ? 12 : 24,
    },
    header: {
      marginTop: isLandscape ? 8 : 20,
      marginBottom: isLandscape ? 8 : 12,
    },
    iconSize: isTablet
      ? tabletIconSize
      : isLandscape
        ? landscapeIconSize
        : baseIconSize,
    title: {
      marginTop: isSmallScreen ? 8 : 12,
      fontSize: isLargeScreen ? 32 : isSmallScreen ? 24 : 28,
    },
    subtitle: {
      marginTop: isSmallScreen ? 4 : 8,
    },
    form: {
      maxWidth: isLargeScreen ? 500 : undefined,
      alignSelf: isLargeScreen ? 'center' : 'stretch',
      width: isLargeScreen ? '100%' : undefined,
    },
    inputContainer: {
      marginBottom: isSmallScreen ? 16 : 24,
    },
    resetButton: {
      marginBottom: isSmallScreen ? 16 : 24,
    },
    helpContainer: {
      flexDirection: isSmallScreen ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: isSmallScreen ? 4 : 0,
    },
    helpText: {
      fontSize: isSmallScreen ? 14 : 16,
    },
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    alignItems: 'center',
  },

  title: {
    fontWeight: 'bold',
  },
  subtitle: {
    textAlign: 'center',
  },
  form: {},
  inputContainer: {},
  resetButton: {},
  helpContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  helpText: {},
  signInButton: {
    marginLeft: 4,
  },
});
